import { create } from 'zustand';
import { MissionScenario, Sector, GameStats } from '../types/game';
import { INITIAL_SECTORS, SCENARIOS } from '../data/sectors';

export type AdminTab = 'general' | 'sectors' | 'scenarios' | 'teams' | 'reference' | 'analytics' | 'training' | 'citations';

interface AdminStore {
  isOpen: boolean;
  activeTab: AdminTab;
  
  // Configurable Settings
  masteryThreshold: number;
  difficultyPreset: 'Easy' | 'Normal' | 'Hard' | 'Custom';
  
  // Scenario Builder
  scenarios: MissionScenario[];
  editingScenarioId: string | null;
  
  // Sector Config
  sectorConfig: Sector[];
  
  // Actions
  toggleDashboard: () => void;
  setTab: (tab: AdminTab) => void;
  setMasteryThreshold: (value: number) => void;
  setDifficulty: (preset: 'Easy' | 'Normal' | 'Hard' | 'Custom') => void;
  updateSectorConfig: (sectorId: string, updates: Partial<Sector>) => void;
  addScenario: (scenario: MissionScenario) => void;
  updateScenario: (scenarioId: string, updates: Partial<MissionScenario>) => void;
  deleteScenario: (scenarioId: string) => void;
  exportScenarios: () => string;
  importScenarios: (json: string) => void;
}

export const useAdminStore = create<AdminStore>((set, get) => ({
  isOpen: false,
  activeTab: 'general',
  
  masteryThreshold: 8.5,
  difficultyPreset: 'Normal',
  
  scenarios: [...SCENARIOS],
  editingScenarioId: null,
  
  sectorConfig: [...INITIAL_SECTORS],
  
  toggleDashboard: () => set(state => ({ isOpen: !state.isOpen })),
  setTab: (tab) => set({ activeTab: tab }),
  
  setMasteryThreshold: (value) => set({ masteryThreshold: value }),
  setDifficulty: (preset) => set({ difficultyPreset: preset }),
  
  updateSectorConfig: (sectorId, updates) => set(state => ({
    sectorConfig: state.sectorConfig.map(s => 
      s.id === sectorId ? { ...s, ...updates } : s
    )
  })),
  
  addScenario: (scenario) => set(state => ({
    scenarios: [...state.scenarios, scenario]
  })),
  
  updateScenario: (scenarioId, updates) => set(state => ({
    scenarios: state.scenarios.map(s => 
      s.id === scenarioId ? { ...s, ...updates } : s
    )
  })),
  
  deleteScenario: (scenarioId) => set(state => ({
    scenarios: state.scenarios.filter(s => s.id !== scenarioId)
  })),
  
  exportScenarios: () => {
    return JSON.stringify(get().scenarios, null, 2);
  },
  
  importScenarios: (json) => {
    try {
      const parsed = JSON.parse(json);
      // Basic validation could go here
      set({ scenarios: parsed });
    } catch (e) {
      console.error('Failed to import scenarios', e);
    }
  }
}));
